import {ObjectReferenceV2} from "./ObjectReferenceV2";
import HierarchiesService from "../../../services/HierarchiesService";
import ImageViewerObject, {ImageViewerObjectAttribute} from "../ImageViewerObject";
import HierarchyNode, {ObjectReferenceWithExtraData, ReferencesDelegate} from "./HierarchyNode";
import {AsyncDataSource} from "../../Misc/Table/AsyncDataSourceGeneric";

const ObjectLabel = "Tag";

export class ReferencesV2 implements ReferencesDelegate {
    private readonly _node: HierarchyNode;

    constructor(node: HierarchyNode) {
        this._node = node;
    }

    getRefsWithExtraDataSource(skipOnSameResult: boolean): AsyncDataSource<ObjectReferenceWithExtraData> {
        const node = this._node;
        let resultRefToSkip = skipOnSameResult ? (
            node.hierarchy.imageViewer.toResultReference()
        ) : undefined;
        return new class implements AsyncDataSource<ObjectReferenceWithExtraData> {
            async values(page: number, limit: number): Promise<ObjectReferenceWithExtraData[]> {
                let response = await HierarchiesService.fetchNodeReferencesExtraDataV2(
                    node.hierarchy, node, page, limit, resultRefToSkip
                );
                return response.data.map(x => ({
                    ...x,
                    reference: new ObjectReferenceV2(node, x.tag_reference)
                }));
            }
        }
    }

    async getObjectAttributes(): Promise<ImageViewerObjectAttribute[] | null> {
        const ref = await this.first();
        return ref?.getObjectAttributes() ?? null;
    }

    isVirtual(): boolean {
        return ["virtual", "Root"].includes(this._node.label);
    }

    async first(): Promise<ObjectReferenceV2 | undefined> {
        const response = await HierarchiesService.getHierarchyNodeReferencesV2First(this._node.hierarchy, this._node);
        if (response.data == null) {
            return undefined;
        } else {
            return new ObjectReferenceV2(this._node, response.data);
        }
    }

    hasOnPageReference() {
        if (!this._node.hierarchy.imageViewer.isLatestResult()) {
            return false;
        }
        const result = this.findReferencedObject() != null;
        return result;
    }

    matchesObject(obj: ImageViewerObject) {
        return obj.label === ObjectLabel && this._node.hasAlias(obj.text);
    }

    findReferencedObject(): ImageViewerObject | null {
        if (!this._node.hierarchy.isInScopeOfTheCurrentPage) {
            return null;
        }
        const matchedObject = (this._node.hierarchy.imageViewer.state.allObjects ?? [])
            .sort((a, b) => Number(b.isPrimary()) - Number(a.isPrimary()))
            .find(obj => this.matchesObject(obj)) ?? null;
        return matchedObject;
    }

    findPotentialObject(): ImageViewerObject | null {
        return this.findReferencedObject();
    }
}
